import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ChevronLeft, ChevronRight, List, Settings, Home,
  Sun, Moon, Type, AlignLeft
} from 'lucide-react';
import { chapterAPI, authAPI } from '../../services/api';
import { useReadingStore, useAuthStore, useUIStore } from '../../hooks/useStore';
import AdDisplay, { ChapterContentWithAds } from '../../components/AdDisplay/AdDisplay';
import './Reading.css';

const Reading = () => {
  const { novelSlug, chapterNumber } = useParams();
  const navigate = useNavigate();
  
  const [chapterData, setChapterData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { 
    fontSize, lineHeight, theme, fontFamily,
    setFontSize, setLineHeight, setTheme, setFontFamily,
    increaseFontSize, decreaseFontSize
  } = useReadingStore();
  
  const { isAuthenticated } = useAuthStore();
  const { isChapterListOpen, isSettingsOpen, toggleChapterList, toggleSettings, closeAll } = useUIStore();
  
  const [chapters, setChapters] = useState([]);
  const [showNav, setShowNav] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Fetch chapter data
  useEffect(() => {
    const fetchChapter = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await chapterAPI.get(novelSlug, chapterNumber);
        setChapterData(response.data);
        
        // Update reading history if authenticated
        if (isAuthenticated && response.data.novel?._id && response.data.chapter?._id) {
          authAPI.updateHistory(response.data.novel._id, response.data.chapter._id).catch(() => {});
        }
      } catch (err) {
        setError(err.message || 'Failed to load chapter');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchChapter();
    window.scrollTo(0, 0);
  }, [novelSlug, chapterNumber, isAuthenticated]);

  // Fetch chapter list
  useEffect(() => {
    const fetchChapters = async () => {
      if (chapterData?.novel?._id) {
        try {
          const response = await chapterAPI.getList(chapterData.novel._id, { limit: 100 });
          setChapters(response.data || []);
        } catch (err) {
          console.error('Error fetching chapters:', err);
        }
      }
    };
    fetchChapters();
  }, [chapterData?.novel?._id]);

  // Handle scroll to hide/show nav
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setShowNav(false);
      } else {
        setShowNav(true);
      }
      setLastScrollY(currentScrollY);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft' && chapterData?.navigation?.prev) {
        navigateToChapter(chapterData.navigation.prev.chapterNumber);
      } else if (e.key === 'ArrowRight' && chapterData?.navigation?.next) {
        navigateToChapter(chapterData.navigation.next.chapterNumber);
      } else if (e.key === 'Escape') {
        closeAll();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterData]);

  const navigateToChapter = useCallback((num) => {
    navigate(`/novel/${novelSlug}/chapter/${num}`);
    closeAll();
  }, [navigate, novelSlug, closeAll]);

  if (isLoading) {
    return (
      <div className="reading-page" data-theme={theme}>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading chapter...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reading-page" data-theme={theme}>
        <div className="error-container">
          <h2>Failed to load chapter</h2>
          <p>{error}</p>
          <Link to={`/novel/${novelSlug}`} className="btn-back">
            Back to novel page
          </Link>
        </div>
      </div>
    );
  }

  const { novel, chapter, navigation } = chapterData || {};

  return (
    <div className="reading-page" data-theme={theme}>
      {/* Top Navigation */}
      <nav className={`reading-nav top ${showNav ? 'visible' : ''}`}>
        <div className="nav-left">
          <Link to={`/novel/${novelSlug}`} className="nav-btn">
            <Home size={20} />
          </Link>
        </div>
        
        <div className="nav-center">
          <h1 className="novel-name">{novel?.title}</h1>
        </div>
        
        <div className="nav-right">
          <button className="nav-btn" onClick={toggleChapterList}>
            <List size={20} />
          </button>
          <button className="nav-btn" onClick={toggleSettings}>
            <Settings size={20} />
          </button>
        </div>
      </nav>

      {/* Chapter Content */}
      <article 
        className="reading-content"
        style={{ 
          fontSize: `${fontSize}px`,
          lineHeight: lineHeight,
          fontFamily: fontFamily === 'serif' ? 'var(--font-reading)' : 'var(--font-body)'
        }}
      >
        {/* Ad - Top of Chapter */}
        

        <header className="chapter-header">
          <span className="chapter-number">Chapter {chapter?.chapterNumber}</span>
          <h2 className="chapter-title">{chapter?.title}</h2>
          <div className="chapter-meta">
            <span>{chapter?.wordCount?.toLocaleString()} words</span>
            <span>•</span>
            <span>{chapter?.views?.toLocaleString()} views</span>
          </div>
        </header>
        <AdDisplay position="chapter_top" className="chapter-ad-top" />
        {/* Chapter Content with Between Paragraph Ads */}
        <div className="chapter-text">
          <ChapterContentWithAds content={chapter?.content || ''} />
        </div>
        
        {chapter?.translatorNotes && (
          <div className="translator-notes">
            <h4>Translator Notes:</h4>
            <p>{chapter.translatorNotes}</p>
          </div>
        )}

        {/* Ad - Bottom of Chapter */}
        <AdDisplay position="chapter_bottom" className="chapter-ad-bottom" />
      </article>

      {/* Bottom Navigation */}
      <nav className={`reading-nav bottom ${showNav ? 'visible' : ''}`}>
        <button 
          className="chapter-btn prev"
          onClick={() => navigateToChapter(navigation?.prev?.chapterNumber)}
          disabled={!navigation?.prev}
        >
          <ChevronLeft size={20} />
          <span>Previous</span>
        </button>

        <button className="chapter-btn list" onClick={toggleChapterList}>
          <List size={20} />
          <span>Index</span>
        </button>

        <button 
          className="chapter-btn next"
          onClick={() => navigateToChapter(navigation?.next?.chapterNumber)}
          disabled={!navigation?.next}
        >
          <span>Next</span>
          <ChevronRight size={20} />
        </button>
      </nav>

      {/* Chapter List Sidebar */}
      <div className={`sidebar-overlay ${isChapterListOpen ? 'active' : ''}`} onClick={closeAll}></div>
      <aside className={`chapter-sidebar ${isChapterListOpen ? 'active' : ''}`}>
        <div className="sidebar-header">
          <h3>Chapter List</h3>
          <button className="close-btn" onClick={toggleChapterList}>×</button>
        </div>
        <div className="chapter-list">
          {chapters.map((ch) => (
            <button
              key={ch._id}
              className={`chapter-item ${ch.chapterNumber === parseInt(chapterNumber) ? 'active' : ''}`}
              onClick={() => navigateToChapter(ch.chapterNumber)}
            >
              <span className="ch-number">Chapter {ch.chapterNumber}</span>
              <span className="ch-title">{ch.title?.replace(/^Chapter \d+:?\s*/i, '')}</span>
            </button>
          ))}
        </div>
      </aside>

      {/* Settings Panel */}
      <div className={`sidebar-overlay ${isSettingsOpen ? 'active' : ''}`} onClick={closeAll}></div>
      <aside className={`settings-panel ${isSettingsOpen ? 'active' : ''}`}>
        <div className="sidebar-header">
          <h3>Reading Settings</h3>
          <button className="close-btn" onClick={toggleSettings}>×</button>
        </div>
        
        <div className="settings-content">
          {/* Theme */}
          <div className="setting-group">
            <label>Theme</label>
            <div className="theme-options">
              <button 
                className={`theme-btn ${theme === 'dark' ? 'active' : ''}`}
                onClick={() => setTheme('dark')}
              >
                <Moon size={18} />
                Dark
              </button>
              <button 
                className={`theme-btn ${theme === 'light' ? 'active' : ''}`}
                onClick={() => setTheme('light')}
              >
                <Sun size={18} />
                Light
              </button>
              <button 
                className={`theme-btn sepia ${theme === 'sepia' ? 'active' : ''}`}
                onClick={() => setTheme('sepia')}
              >
                📜
                Sepia
              </button>
            </div>
          </div>

          {/* Font Size */}
          <div className="setting-group">
            <label>
              <Type size={16} />
              Font Size: {fontSize}px
            </label>
            <div className="size-controls">
              <button onClick={decreaseFontSize}>A-</button>
              <input 
                type="range" 
                min="12" 
                max="32" 
                value={fontSize}
                onChange={(e) => setFontSize(parseInt(e.target.value))}
              />
              <button onClick={increaseFontSize}>A+</button>
            </div>
          </div>

          {/* Line Height */}
          <div className="setting-group">
            <label>
              <AlignLeft size={16} />
              Line Height: {lineHeight}
            </label>
            <input 
              type="range" 
              min="1.2" 
              max="3" 
              step="0.1"
              value={lineHeight}
              onChange={(e) => setLineHeight(parseFloat(e.target.value))}
            />
          </div>

          {/* Font Family */}
          <div className="setting-group">
            <label>Font Family</label>
            <div className="font-options">
              <button 
                className={`font-btn ${fontFamily === 'default' ? 'active' : ''}`}
                onClick={() => setFontFamily('default')}
              >
                Sans Serif
              </button>
              <button 
                className={`font-btn ${fontFamily === 'serif' ? 'active' : ''}`}
                onClick={() => setFontFamily('serif')}
                style={{ fontFamily: 'var(--font-reading)' }}
              >
                Serif
              </button>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
};

export default Reading;
